import ProjectOrganisation from "./models/project-organisation";
import ProjectOrganisationBuilding from "./models/project-organisation-building";
import ProjectOrganisationStorey from "./models/project-organisation-storey";
import FlowMode from "@/models/enums/flow-mode";
import SetupFileStoreyData from "./models/setup-file-data";

const calculateStoreyIndexes = function (storeys: ProjectOrganisationStorey[]): { id: string; index: number; }[] {
    const basementStoreys = storeys.filter(s => s.storeyType === 'INFRA');
    const superStoreys = storeys.filter(s => s.storeyType === 'SUPER' || s.storeyType === 'TOIT');

    basementStoreys.sort((a, b) => b.altimetry - a.altimetry);
    superStoreys.sort((a, b) => a.altimetry - b.altimetry);

    const updates: { id: string; index: number }[] = [];

    basementStoreys.forEach((storey, idx) => {
        updates.push({ id: storey.id, index: idx + 1 });
    });

    superStoreys.forEach((storey, idx) => {
        updates.push({ id: storey.id, index: idx });
    });

    return updates;
};

const applySetupFiles = function(
    project: ProjectOrganisation,
    filesData: SetupFileStoreyData[],
    flowMode: FlowMode
) {
    let building = project.buildings[0];
    if (!building)
        building = project.addBuilding("Nouveau bâtiment");

    const defaultAltimetry = 2.7;
    let lastStoreys = building.storeys.length;

    // Rajout de storey TOIT pour un projet PDF simple
    if (flowMode === FlowMode._2D3D) {
        const roofStorey = building.storeys.find(
            s => s.storeyType === "TOIT"
        );

        if (!roofStorey) {
            project.addStorey(
                building.id,
                "",
                defaultAltimetry * (filesData.length + lastStoreys),
                "TOIT"
            );
        } else {
            lastStoreys -= 1;
            roofStorey.altimetry =
                defaultAltimetry * (filesData.length + lastStoreys);
        }
    }

    filesData.forEach((fileData, index) => {
        const addedStorey = project.addStorey(
            building.id,
            fileData.file.name,
            defaultAltimetry * (index + lastStoreys),
            "SUPER"
        );

        if (addedStorey) {
            project.addStoreyFile(
                building.id,
                addedStorey.id,
                fileData.fileData
            );
        }
    });

    const updates = calculateStoreyIndexes(building.storeys);

    updates.forEach(update => {
        const storey = building.storeys.find(
            s => s.id === update.id
        );

        if (storey)
            storey.index = update.index;
    });

    project.setDirtyFlag(true);
}

const appendRoofsToOrganisation = function (
    project: ProjectOrganisation
): ProjectOrganisation {
    const updated = new ProjectOrganisation();
    updated.files2d = [...project.files2d];
    updated.files3d = [...project.files3d];
    updated.isDirty = true;

    updated.buildings = project.buildings.map((b: ProjectOrganisationBuilding) => {
        const building = new ProjectOrganisationBuilding(b.id, b.name, b.index);
        building.files2d = [...b.files2d];
        building.files3d = [...b.files3d];
        building.file3dFrom2d = b.file3dFrom2d;
        building.storeys = b.storeys.map((s: ProjectOrganisationStorey) => {
            const storey = new ProjectOrganisationStorey(s.id, s.name, s.altimetry, s.storeyType, s.index);
            storey.files2d = [...s.files2d];
            storey.files3d = [...s.files3d];
            return storey;
        });

        if (building.storeys.find((s: ProjectOrganisationStorey) => s.storeyType === "TOIT") == null) {
            const maxAlt = building.storeys.length > 0
                ? building.storeys.reduce((max: number, storey: ProjectOrganisationStorey) => Math.max(max, storey.altimetry), building.storeys[0].altimetry)
                : 0;
            const newAltimetry = (building.storeys.length > 0 ? maxAlt : 0) + 2.7;
            building.addStorey('Toiture', newAltimetry, 'TOIT');
            const updates = calculateStoreyIndexes(building.storeys);
            updates.forEach(update => {
                const storey = building.storeys.find((s: ProjectOrganisationStorey) => s.id === update.id);
                if (storey) {
                    storey.index = update.index;
                }
            });
        }

        return building;
    });

    return updated;
};

export { calculateStoreyIndexes, applySetupFiles, appendRoofsToOrganisation }